import { Injectable } from '@angular/core';

const VALIDATION_PLAINTEXT = 'decrypted';
const PASSPHRASE_STORAGE_PREFIX = 'examLockPassphrase_';

@Injectable({
  providedIn: 'root'
})
export class ExamLockCryptoService {
  private validationFields = new Map<number, string>();

  isValidPassphraseFormat(passphrase: string): boolean {
    return !!passphrase && passphrase.length > 0 && passphrase.length <= 128;
  }

  getCachedPassphrase(examId: number): string {
    return sessionStorage.getItem(`${PASSPHRASE_STORAGE_PREFIX}${examId}`) || '';
  }

  cachePassphrase(examId: number, passphrase: string) {
    sessionStorage.setItem(`${PASSPHRASE_STORAGE_PREFIX}${examId}`, passphrase);
  }

  clearPassphrase(examId: number) {
    sessionStorage.removeItem(`${PASSPHRASE_STORAGE_PREFIX}${examId}`);
    this.validationFields.delete(examId);
  }

  clearAllPassphrases() {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(PASSPHRASE_STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
    this.validationFields.clear();
  }

  cacheValidationField(examId: number, decryptValidationField: string) {
    this.validationFields.set(examId, decryptValidationField || '');
  }

  getCachedValidationField(examId: number): string {
    return this.validationFields.get(examId) || '';
  }

  async encrypt(plaintext: string, passphrase: string): Promise<string> {
    if (!plaintext) {
      return plaintext;
    }
    const key = await this.importKey(passphrase);
    const iv = crypto.getRandomValues(new Uint8Array(16));
    const data = new TextEncoder().encode(plaintext);
    const cipher = await crypto.subtle.encrypt({ name: 'AES-CBC', iv }, key, data);
    const combined = new Uint8Array(iv.length + cipher.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(cipher), iv.length);
    return this.bytesToBase64(combined);
  }

  async decrypt(ciphertextBase64: string, passphrase: string): Promise<string> {
    if (!ciphertextBase64) {
      return ciphertextBase64;
    }
    const key = await this.importKey(passphrase);
    const combined = this.base64ToBytes(ciphertextBase64);
    const iv = combined.subarray(0, 16);
    const cipher = combined.subarray(16);
    const plain = await crypto.subtle.decrypt({ name: 'AES-CBC', iv }, key, cipher);
    return new TextDecoder().decode(plain);
  }

  async validatePassphrase(passphrase: string, decryptValidationField: string): Promise<boolean> {
    if (!this.isValidPassphraseFormat(passphrase) || !decryptValidationField) {
      return false;
    }
    try {
      return await this.decrypt(decryptValidationField, passphrase) === VALIDATION_PLAINTEXT;
    } catch {
      return false;
    }
  }

  async decryptQuestionsHtml(questionsHtml: any[], passphrase: string): Promise<any[]> {
    for (const item of questionsHtml) {
      if (!item?.question?.locked) {
        continue;
      }
      if (item?.question?.questionhtml) {
        item.question.questionhtml = await this.decrypt(item.question.questionhtml, passphrase);
      }
      if (item?.options) {
        for (const option of item.options) {
          if (option.optionhtml) {
            option.optionhtml = await this.decrypt(option.optionhtml, passphrase);
          }
        }
      }
      if (item?.essaysubquestions) {
        for (const sub of item.essaysubquestions) {
          if (sub.questionhtml) {
            sub.questionhtml = await this.decrypt(sub.questionhtml, passphrase);
          }
        }
      }
    }
    return questionsHtml;
  }

  private async importKey(passphrase: string): Promise<CryptoKey> {
    const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(passphrase));
    return crypto.subtle.importKey('raw', hash, { name: 'AES-CBC' }, false, ['encrypt', 'decrypt']);
  }

  private bytesToBase64(bytes: Uint8Array): string {
    let binary = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return btoa(binary);
  }

  private base64ToBytes(value: string): Uint8Array {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }
}
